import {
    ArrowBack as ArrowBackIcon,
    Cancel as CancelIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Container,
    FormControl,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

const CourseUpdate = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [courseData, setCourseData] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const apiURL = process.env.REACT_APP_API;
    const navigate = useNavigate();
    const location = useLocation();

    // Extract courseid from URL query parameters
    const getCourseIdFromURL = () => {
        const params = new URLSearchParams(location.search);
        return params.get('courseid');
    };

    const courseId = getCourseIdFromURL();

    // Yup validation schema
    const validationSchema = Yup.object({
        c_title: Yup.string()
            .min(3, 'Course title must be at least 3 characters')
            .required('Course title is required'),
        c_name: Yup.string()
            .required('Course name is required'),
        c_description: Yup.string()
            .min(10, 'Description must be at least 10 characters')
            .required('Course description is required'),
        c_duration: Yup.number()
            .positive('Duration must be greater than 0')
            .required('Duration is required'),
        c_max_price: Yup.number()
            .positive('Maximum price must be greater than 0')
            .required('Maximum price is required'),
        c_price: Yup.number()
            .positive('Current price must be greater than 0')
            .test('price-less-than-max', 'Current price cannot be higher than maximum price', function (value) {
                return value <= this.parent.c_max_price;
            })
            .required('Current price is required'),
        c_coupon: Yup.string(),
        c_coupon_amount: Yup.number()
            .min(0, 'Coupon amount must be 0 or greater')
            .test('coupon-amount-less-than-price', 'Coupon amount cannot be higher than current price', function (value) {
                if (!value) return true;
                return value <= this.parent.c_price;
            }),
        c_status: Yup.string()
            .oneOf(['pending', 'active', 'archive'], 'Invalid status')
            .required('Status is required'),
        website_content: Yup.string()
            .min(20, 'Website content must be at least 20 characters')
            .required('Website content is required')
    });

    // Fetch course details
    useEffect(() => {
        const fetchCourseDetails = async () => {
            if (!courseId) {
                toast.error('No course ID provided');
                navigate('/courses');
                return;
            }

            try {
                setIsLoading(true);
                const response = await axios.get(`${apiURL}/admin/course/details`, {
                    params: { courseid: courseId }
                });

                if (response.data.status === 'success' || response.data.status === true) {
                    const course = response.data.data;
                    setCourseData(course);

                    // Set formik initial values with fetched data
                    formik.setValues({
                        c_title: course.c_title || '',
                        c_name: course.c_name || '',
                        c_description: course.c_description || '',
                        c_duration: course.c_duration || '',
                        c_max_price: course.c_max_price || '',
                        c_price: course.c_price || '',
                        c_coupon: course.c_coupon || '',
                        c_coupon_amount: course.c_coupon_amount || '',
                        c_status: course.c_status || 'pending',
                        website_content: course.website_content || ''
                    });
                } else {
                    toast.error(response.data.message || 'Failed to fetch course details');
                    navigate(-1);
                }
            } catch (error) {
                console.error('Error fetching course details:', error);
                toast.error(error.response?.data?.message || 'An error occurred while fetching course details');
                navigate(-1);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCourseDetails();
    }, [courseId, apiURL, navigate]);

    // Formik initialization
    const formik = useFormik({
        initialValues: {
            c_title: '',
            c_name: '',
            c_description: '',
            c_duration: '',
            c_max_price: '',
            c_price: '',
            c_coupon: '',
            c_coupon_amount: '',
            c_status: 'pending',
            website_content: ''
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setIsSubmitting(true);

            try {
                const updateData = {
                    ...values,
                    courseid: courseId
                };

                const response = await axios.post(`${apiURL}/admin/course/update`, updateData);

                if (response.data.status === 'success' || response.data.status === true) {
                    toast.success(response.data.message || 'Course updated successfully!');
                    navigate(-1);

                } else {
                    toast.error(response.data.message || 'Failed to update course');
                }
            } catch (error) {
                console.error('Error updating course:', error);
                toast.error(error.response?.data?.message || 'An error occurred while updating the course');
            } finally {
                setIsSubmitting(false);
            }
        }
    });

    // Handle back navigation
    const handleBack = () => {
        navigate(-1);
    };

    // Show loading state
    if (isLoading) {
        return (
            <Box className='content' pb={5}>
                <Container>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <Alert severity="info" sx={{ maxWidth: 400 }}>
                            Loading course details...
                        </Alert>
                    </Box>
                </Container>
            </Box>
        );
    }

    // Show error if no course data
    if (!courseData) {
        return (
            <Box className='content' pb={5}>
                <Container>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <Alert severity="error" sx={{ maxWidth: 400 }}>
                            Course not found. Please check the course ID.
                        </Alert>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box className='content' pb={5}>
            <Container>
                {/* Header with Back Button */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }} gap={2} alignItems={{ md: 'center', xs: 'start' }} flexDirection={{ md: 'row', xs: 'column' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={'bold'}>
                            Update Course
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Course ID: {courseId}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        disabled={isSubmitting}
                    >
                        Back to Courses
                    </Button>
                </Box>

                {/* Course Info Alert */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        You are editing: <strong>{courseData.c_title}</strong>
                    </Typography>
                </Alert>

                {/* Form Container */}
                <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                    <Stack spacing={3}>
                        {/* Course Title */}
                        <TextField
                            required
                            fullWidth
                            label="Course Title"
                            name="c_title"
                            value={formik.values.c_title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.c_title && Boolean(formik.errors.c_title)}
                            helperText={formik.touched.c_title && formik.errors.c_title}
                            placeholder="e.g., Introduction to React"
                            disabled={isSubmitting}
                        />

                        {/* Course Name */}
                        <TextField
                            required
                            fullWidth
                            label="Course Name"
                            name="c_name"
                            value={formik.values.c_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.c_name && Boolean(formik.errors.c_name)}
                            helperText={formik.touched.c_name && formik.errors.c_name}
                            placeholder="e.g., react-basics"
                            disabled={isSubmitting}
                        />

                        {/* Course Description */}
                        <TextField
                            required
                            fullWidth
                            multiline
                            rows={4}
                            label="Course Description"
                            name="c_description"
                            value={formik.values.c_description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.c_description && Boolean(formik.errors.c_description)}
                            helperText={formik.touched.c_description && formik.errors.c_description}
                            placeholder="Describe what students will learn in this course..."
                            disabled={isSubmitting}
                        />

                        {/* Duration and Status Row */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* Duration */}
                            <FormControl
                                fullWidth
                                required
                                disabled={isSubmitting}
                                error={formik.touched.c_duration && Boolean(formik.errors.c_duration)}
                            >
                                <InputLabel>Duration (Months)</InputLabel>
                                <Select
                                    name="c_duration"
                                    value={formik.values.c_duration}
                                    label="Duration (Months)"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value={1}>1 Month</MenuItem>
                                    <MenuItem value={3}>3 Months</MenuItem>
                                    <MenuItem value={6}>6 Months</MenuItem>
                                    <MenuItem value={9}>9 Months</MenuItem>
                                    <MenuItem value={12}>1 Year</MenuItem>
                                    <MenuItem value={18}>1.5 Years</MenuItem>
                                    <MenuItem value={24}>2 Years</MenuItem>
                                </Select>
                                {formik.touched.c_duration && formik.errors.c_duration && (
                                    <FormHelperText>{formik.errors.c_duration}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Status */}
                            <FormControl
                                fullWidth
                                required
                                disabled={isSubmitting}
                                error={formik.touched.c_status && Boolean(formik.errors.c_status)}
                            >
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="c_status"
                                    value={formik.values.c_status}
                                    label="Status"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="archive">Archive</MenuItem>
                                </Select>
                                {formik.touched.c_status && formik.errors.c_status && (
                                    <FormHelperText>{formik.errors.c_status}</FormHelperText>
                                )}
                            </FormControl>
                        </Stack>

                        {/* Pricing Row */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* Maximum Price */}
                            <TextField
                                required
                                fullWidth
                                type="number"
                                label="Maximum Price"
                                name="c_max_price"
                                value={formik.values.c_max_price}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.c_max_price && Boolean(formik.errors.c_max_price)}
                                helperText={formik.touched.c_max_price && formik.errors.c_max_price}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    inputProps: { min: 0, step: 1 }
                                }}
                                placeholder="0"
                                disabled={isSubmitting}
                            />

                            {/* Current Price */}
                            <TextField
                                required
                                fullWidth
                                type="number"
                                label="Current Price"
                                name="c_price"
                                value={formik.values.c_price}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.c_price && Boolean(formik.errors.c_price)}
                                helperText={formik.touched.c_price && formik.errors.c_price}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    inputProps: { min: 0, step: 1 }
                                }}
                                placeholder="0"
                                disabled={isSubmitting}
                            />
                        </Stack>

                        {/* Coupon Row */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* Coupon Code */}
                            <TextField
                                fullWidth
                                label="Coupon Code (Optional)"
                                name="c_coupon"
                                value={formik.values.c_coupon}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.c_coupon && Boolean(formik.errors.c_coupon)}
                                helperText={formik.touched.c_coupon && formik.errors.c_coupon}
                                placeholder="e.g., SAVE20"
                                disabled={isSubmitting}
                            />

                            {/* Coupon Amount */}
                            <TextField
                                fullWidth
                                type="number"
                                label="Coupon Discount Amount (Optional)"
                                name="c_coupon_amount"
                                value={formik.values.c_coupon_amount}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.c_coupon_amount && Boolean(formik.errors.c_coupon_amount)}
                                helperText={formik.touched.c_coupon_amount && formik.errors.c_coupon_amount}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    inputProps: { min: 0, step: 1 }
                                }}
                                placeholder="0"
                                disabled={isSubmitting}
                            />
                        </Stack>

                        {/* Price Info Alert */}
                        {formik.values.c_max_price && formik.values.c_price && !formik.errors.c_price && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                Discount: ₹{(parseFloat(formik.values.c_max_price) - parseFloat(formik.values.c_price)).toFixed(0)} ({((parseFloat(formik.values.c_max_price) - parseFloat(formik.values.c_price)) / parseFloat(formik.values.c_max_price) * 100).toFixed(1)}% off)
                            </Alert>
                        )}

                        {/* Last Updated Info */}
                        {courseData.updated_at && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    Last updated: {new Date(courseData.updated_at).toLocaleString()}
                                </Typography>
                            </Alert>
                        )}

                        {/* Website Content - Rich Text Editor */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                                Website Content <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <Box
                                sx={{
                                    '& .quill': {
                                        bgcolor: 'background.paper',
                                        borderRadius: 1,
                                        border: formik.touched.website_content && formik.errors.website_content ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                                        '&:hover': {
                                            borderColor: formik.touched.website_content && formik.errors.website_content ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)'
                                        }
                                    },
                                    '& .ql-toolbar': {
                                        borderTopLeftRadius: 4,
                                        borderTopRightRadius: 4,
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                        bgcolor: 'action.hover'
                                    },
                                    '& .ql-container': {
                                        borderBottomLeftRadius: 4,
                                        borderBottomRightRadius: 4,
                                        minHeight: '200px',
                                        fontSize: '14px'
                                    },
                                    '& .ql-editor': {
                                        minHeight: '200px'
                                    }
                                }}
                            >
                                <ReactQuill
                                    theme="snow"
                                    value={formik.values.website_content}
                                    onChange={(content) => formik.setFieldValue('website_content', content)}
                                    onBlur={() => formik.setFieldTouched('website_content', true)}
                                    placeholder="Enter detailed course content for the website..."
                                    modules={{
                                        toolbar: [
                                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                            ['bold', 'italic', 'underline', 'strike'],
                                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                                            [{ 'color': [] }, { 'background': [] }],
                                            [{ 'align': [] }],
                                            ['link', 'image'],
                                            ['clean']
                                        ]
                                    }}
                                />
                            </Box>
                            {formik.touched.website_content && formik.errors.website_content && (
                                <FormHelperText error sx={{ ml: 2 }}>
                                    {formik.errors.website_content}
                                </FormHelperText>
                            )}
                        </Box>

                        {/* Form Actions */}
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={() => formik.resetForm()}
                                disabled={isSubmitting}
                                color="secondary"
                            >
                                Reset Changes
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                startIcon={isSubmitting ? null : <SaveIcon />}
                                disabled={isSubmitting}
                                sx={{ minWidth: 120 }}
                            >
                                {isSubmitting ? 'Updating...' : 'Update Course'}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                {/* Form Status Indicator */}
                {isSubmitting && (
                    <Alert severity="info" sx={{ mt: 3 }}>
                        Updating course. Please wait...
                    </Alert>
                )}

            </Container>
        </Box>
    );
};

export default CourseUpdate;